'use client'

import { useEffect, useState } from 'react'
import {
  Apple,
  Code2,
  Download as DownloadIcon,
  Monitor,
  Smartphone,
  Terminal,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

type PlatformKey = 'windows' | 'macos' | 'linux' | 'android' | 'ios'

type DownloadLink = {
  version: string
  href: string
  alternatives?: DownloadLink[]
}

type LatestDownloads = Partial<Record<PlatformKey, DownloadLink>>

type DownloadSummary = {
  downloads?: Record<string, number>
  totals?: Record<string, number>
  kinds?: Record<string, string>
  latest?: LatestDownloads | null
}

type LinuxPackageHelp = {
  suffix: string
  label: string
  detail: string
  command?: (href: string) => string
}

const linuxPackageHelp: LinuxPackageHelp[] = [
  {
    suffix: '.deb',
    label: 'Download DEB',
    detail: 'Ubuntu / Debian / Mint',
  },
  {
    suffix: '.rpm',
    label: 'Download RPM',
    detail: 'Fedora / RHEL / openSUSE',
  },
  {
    suffix: '.AppImage',
    label: 'Download AppImage',
    detail: 'Portable Linux or double-click run',
  },
  {
    suffix: '.pkg.tar.zst',
    label: 'Download Pacman',
    detail: 'Arch / Omarchy / Manjaro',
    command: (href: string) => `sudo pacman -U ~/Downloads/${href.split('/').pop()}`,
  },
]

function linuxDownloadHelp(href: string) {
  return linuxPackageHelp.find((item) => href.endsWith(item.suffix))
}

function linuxDownloadLinks(download: DownloadLink) {
  const links: { link: DownloadLink; help: LinuxPackageHelp }[] = []
  for (const link of [download, ...(download.alternatives ?? [])]) {
    const help = linuxDownloadHelp(link.href)
    if (help) links.push({ link, help })
  }
  return links
}

const platforms = [
  {
    key: 'macos' as const,
    name: 'macOS',
    icon: Apple,
    detail: 'macOS 12 or newer · Apple Silicon',
    cta: 'Download for macOS',
    note: 'The signed app installs from a standard DMG.',
  },
  {
    key: 'windows' as const,
    name: 'Windows',
    icon: Monitor,
    detail: 'Windows 10 & 11 · 64-bit',
    cta: 'Download for Windows',
    note: 'Windows builds will appear here as soon as the first installer is published.',
  },
  {
    key: 'linux' as const,
    name: 'Linux',
    icon: Terminal,
    detail: 'DEB · RPM · AppImage · Pacman',
    cta: 'Download for Linux',
    note: 'Choose the package format that matches your distribution.',
  },
  {
    key: 'android' as const,
    name: 'Android',
    icon: Smartphone,
    detail: 'Android 10 or newer · Phone & tablet',
    cta: 'Download for Android',
    note: 'Install the signed APK directly from NewEmail.',
  },
  {
    key: 'ios' as const,
    name: 'iOS',
    icon: Apple,
    detail: 'iPhone & iPad · iOS 16 or newer',
    cta: 'Download for iOS',
    note: 'The App Store build will appear here when published.',
  },
]

export function Download({ standalone = false }: { standalone?: boolean }) {
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [totals, setTotals] = useState<Record<string, number>>({})
  const [kinds, setKinds] = useState<Record<string, string>>({})
  const [latest, setLatest] = useState<LatestDownloads>({})

  useEffect(() => {
    let cancelled = false
    fetch('/api/downloads', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: DownloadSummary | null) => {
        if (cancelled || !data) return
        setCounts(data.downloads ?? {})
        setTotals(data.totals ?? {})
        setKinds(data.kinds ?? {})
        setLatest(data.latest ?? {})
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const countLabel = (href: string) => {
    const kind = kinds[href]
    const value = kind && typeof totals[kind] === 'number' ? totals[kind] : counts[href]
    return typeof value === 'number' ? `${value.toLocaleString()} downloads` : null
  }

  return (
    <section
      id="download"
      className={`border-y border-border/70 bg-card ${standalone ? 'py-16 md:py-24' : 'py-16 md:py-20'}`}
    >
      <div className="mx-auto max-w-7xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <span className="font-mono text-sm font-semibold text-primary">// downloads</span>
          <h2 className="mt-3 text-balance text-3xl font-extrabold tracking-tight sm:text-4xl">
            Choose your platform.
          </h2>
          <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">
            Release files are discovered automatically, so this page always points at the newest
            published build for each platform.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {platforms.map((platform) => {
            const Icon = platform.icon
            const download = latest[platform.key]
            const linuxLinks =
              platform.key === 'linux' && download ? linuxDownloadLinks(download) : []
            return (
              <div
                key={platform.key}
                className="flex h-full flex-col rounded-2xl border border-border bg-background p-6 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg"
              >
                <div className="flex items-start justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-card text-primary">
                    <Icon className="h-6 w-6" />
                  </span>
                  {download?.version ? (
                    <span className="rounded-full border border-primary/25 bg-secondary px-2.5 py-1 font-mono text-[11px] font-bold text-primary">
                      v{download.version}
                    </span>
                  ) : (
                    <span className="rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
                      Source available
                    </span>
                  )}
                </div>
                <h3 className="mt-6 text-xl font-bold tracking-tight">{platform.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{platform.detail}</p>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{platform.note}</p>
                <div className="mt-auto flex flex-col gap-2 pt-7">
                  {platform.key === 'linux' && download ? (
                    linuxLinks.map(({ link, help }) => {
                      const installCommand = help.command?.(link.href)
                      return (
                        <div
                          key={link.href}
                          className="rounded-xl border border-border bg-card/70 p-3"
                        >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="text-xs font-semibold text-foreground">{help.detail}</p>
                              <p className="mt-1 font-mono text-[10px] tabular-nums text-muted-foreground">
                                {countLabel(link.href) ?? ''}
                              </p>
                            </div>
                            <Button
                              variant={link.href === download.href ? 'default' : 'outline'}
                              size="sm"
                              nativeButton={false}
                              render={<a href={link.href} />}
                            >
                              <DownloadIcon className="h-4 w-4" />
                              {help.label}
                            </Button>
                          </div>
                          {installCommand ? (
                            <button
                              type="button"
                              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-left font-mono text-[10px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                              onClick={() => {
                                void navigator.clipboard?.writeText(installCommand)
                              }}
                            >
                              {installCommand}
                            </button>
                          ) : null}
                        </div>
                      )
                    })
                  ) : download ? (
                    <Button
                      size="lg"
                      className="w-full"
                      nativeButton={false}
                      render={<a href={download.href} />}
                    >
                      <DownloadIcon className="h-4 w-4" />
                      {platform.cta}
                    </Button>
                  ) : (
                    <span className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-border bg-muted px-3 text-sm font-medium text-muted-foreground">
                      <DownloadIcon className="h-4 w-4" />
                      Coming soon
                    </span>
                  )}
                  <span className="min-h-4 text-center font-mono text-[10px] tabular-nums text-muted-foreground">
                    {download?.href && platform.key !== 'linux'
                      ? (countLabel(download.href) ?? '')
                      : platform.key !== 'linux'
                        ? 'Build from source'
                        : ''}
                  </span>
                  {platform.key !== 'linux' && download?.alternatives?.map((alt) => (
                    <Button
                      key={alt.href}
                      variant="outline"
                      size="sm"
                      nativeButton={false}
                      render={<a href={alt.href} />}
                    >
                      {alt.href.endsWith('.rpm') ? 'Download RPM' : 'Download AppImage'}
                    </Button>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-xl border border-border bg-secondary/60 px-5 py-4 text-sm sm:flex-row">
          <div>
            <p className="font-semibold">One inbox. Every screen.</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Windows, macOS, Linux, Android, and iOS share the same NewEmail experience.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            nativeButton={false}
            render={
              <a
                href="https://github.com/ricktek954/newemail"
                target="_blank"
                rel="noreferrer"
              />
            }
          >
            <Code2 className="h-4 w-4" />
            View source
          </Button>
        </div>
      </div>
    </section>
  )
}
